/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xAppState } from "@monsma/App/Store/Reducers";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import Search from "@monsma/XDL/Icons/Search";
import * as cssLinter from "css";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: xAppState) => ({
    notificationBar: state.xHeader.notificationBar,
    pageType: getCurrentPage(state).type, // xng-change
    drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
});

const mapDispatchToProps = {
    setNavDrawerIsOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HeaderSearchInputStyles {
    mobileSearchClickable?: ClickablePresentationProps;
    inputWrapper?: InjectableCss;
    searchInputStyles?: SearchInputStyles;
    searchIconStyles?: IconProps;
    searchWrapper?: InjectableCss;
}

export const searchInputStyles: HeaderSearchInputStyles = {
    mobileSearchClickable: {
        css: css`
            padding: 5px;
        `,
    },
    searchWrapper: {
        css: css`
            padding: 17px 7px;
            ${screen("gt", "md")} {
                display: none;
            }

            &.hide {
                display: none;
            }
        `,
    },
    inputWrapper: {
        css: css`
            ${screen("lte", "md")} {
                display: none;
            }
        `,
    },
    searchIconStyles: {
        src: Search,
        size: 20,
        color: "common.ui9",
    },
};

const styles = searchInputStyles;

const defaultCustomCss = `.search-input{
}

.popover{
}

.autocomplete-wrapper{
}

.autocomplete-column-wrapper{
}

.autocomplete-categories-header{
}

.autocomplete-categories-item-text{
}

.autocomplete-categories-item-link{
}

.autocomplete-categories-link{
}

.autocomplete-brands-header{
}

.autocomplete-brands-item-text{
}

.autocomplete-brands-item-link{
}

.autocomplete-brands-link{
}

.autocomplete-content-header{
}

.autocomplete-content-link{
}

.autocomplete-products-header{
}

.autocomplete-products-container{
}

.autocomplete-products-image-grid-item{
}

.autocomplete-products-image{
}

.autocomplete-products-info-grid-item{
}

.autocomplete-products-link{
}

.autocomplete-products-erp-number{
}`;

const HeaderSearchInput: React.FC<Props & { isAnimated?: boolean }> = ({
    id,
    isAnimated = false,
    fields,
    notificationBar,
    pageType,
    drawerIsOpen,
    setNavDrawerIsOpen,
}) => {
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const [mobileInputOpen, setMobileInputOpen] = React.useState(false);
    const inputRef = React.useRef(null);
    const isCheckoutPage = isItCheckoutPage(pageType);

    React.useEffect(() => {
        if (mobileInputOpen && inputRef.current) {
            (inputRef.current as unknown as HTMLElement)?.focus();
        }
        if (drawerIsOpen && mobileInputOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
    }, [mobileInputOpen]);

    React.useEffect(() => {
        if (drawerIsOpen && mobileInputOpen) {
            setMobileInputOpen(!mobileInputOpen);
            document.querySelector("body")?.classList?.remove("shade");
        }
    }, [drawerIsOpen]);

    const mobileSearchButtonClick = () => {
        setMobileInputOpen(!mobileInputOpen);
        if (!mobileInputOpen) {
            document.querySelector("body")?.classList?.add("shade");
        } else {
            document.querySelector("body")?.classList?.remove("shade");
        }
    };

    // xng-change: Update the mobile style to display a clickable search icon to open the search input
    return (
        <>
            <StyledWrapper {...styles.searchWrapper} className={`${isCheckoutPage ? "hide" : ""}`}>
                <Clickable {...styles.mobileSearchClickable} onClick={() => mobileSearchButtonClick()}>
                    <Icon {...styles.searchIconStyles} />
                    <VisuallyHidden>{translate("Search")}</VisuallyHidden>
                </Clickable>
            </StyledWrapper>
            <StyledWrapper
                className={isCheckoutPage ? "hide" : mobileInputOpen ? "display-mobile-input" : ""}
                {...({ notificationBar } as any)}
                css={css`
                    &.hide {
                        display: none;
                    }
                    ${screen("lte", "md")} {
                        display: none;
                        &.display-mobile-input {
                            display: block;
                            position: fixed;
                            left: 0;
                            right: 0;
                            top: 64px;
                            ${({ notificationBar }: any) => notificationBar && `top: calc(64px + 40px);`}
                        }
                    }
                `}
            >
                <SearchInput
                    id={id}
                    isAnimated={isAnimated}
                    extendedStyles={styles.searchInputStyles}
                    inputRef={inputRef}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderSearchInput),
    definition: {
        group: "Header",
        displayName: "Search Input",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
